/* eslint-disable react-refresh/only-export-components */

import React, { useCallback, useMemo, useState } from 'react';
import { IContextualMenuItem, IconButton } from '@fluentui/react';
import { CONTRACT_EXISTS } from '../../context/network/http/QueryProvider/queryKeys';
import { ApiCalls } from '../../api/apiv2';
import { useUISettings } from '../../context/network/http/QueryProvider/queries/UISettings';
import { useQuery } from '@tanstack/react-query';
import { WithStore } from '../../Services/StateStore';
import { GridType, ResourceType, Sticky, UserType, VersionResolution, WorkType } from '../../api/generated/data-contracts';
import { StoreProps } from '../../Services/StoreProps';
import { RowMenuItemInfo } from '../../Entities/Dto/RowMenuItemInfo';
import { PluginIDs } from '../../Utilities/PluginIDs';
import { guid } from '../../helpers/guid';
import { ContractInformationDialog } from './ContractInformationDialog';
import { CustomContractPropertiesDialog } from './CustomContractPropertiesDialog';
import { MoveContractDialog } from './MoveContractDialog';
import { Plugin } from '../../Services/PluginInvoker';
import { CountDownConfirm } from '../../Services/CountDownConfirm';
import { DataService } from '../../Services/DataService';
import { OverviewContext } from '../../Utilities/Context/OverviewContext';
import { DebugOverrides } from '../../Utilities/DebugOverrides';
import { useStore } from '../../context/store';
import { EntityType } from '../../Entities/EntityTypes';
import { IBlockContent } from '../../context/contexts/pageBlockContext/pageBlockContext';
import { useTPPermissions } from '../../hooks/usePermissions';
import { useTranslation } from '../../Locales/i18n/localizationService';

export interface IContractRowMenuProps extends StoreProps {
    ContractId: string;
    GridType: GridType;
    IsProjectOwner: boolean;
    AllProperties: { [key: string]: any };
}

const ContractRowMenu: React.FC<IContractRowMenuProps> = ({
    AllProperties: allProperties,
    ContractId: contractId,
    GridType: gridType,
    IsProjectOwner: isProjectOwner,
    Store: store,
}) => {
    const { data: uiSettings } = useUISettings();
    const [selectedSticky, setSelectedSticky] = useState(allProperties.Sticky);

    const { setBlockDialog } = useStore(store => ({
        setBlockDialog: store.setBlockDialog,
    }));

    const { data: contractExists, refetch } = useQuery({
        queryKey: [CONTRACT_EXISTS, contractId],
        queryFn: () => ApiCalls.contractExists(contractId).then(res => res.data),
        staleTime: 0, // Set to 0 if you want it to always refetch
        enabled: false, // Start disabled
    });

    const openDialog = useCallback(
        (content: JSX.Element, options: Omit<IBlockContent, 'content'>) => {
            setBlockDialog(content, options);
        },
        [setBlockDialog],
    );

    const menuItems = useMenuItems({
        contractId,
        gridType,
        isProjectOwner,
        allProperties,
        uiSettings,
        contractExists,
        selectedSticky,
        setSelectedSticky,
        store,
        openDialog,
    });

    return (
        <IconButton
            className="tp-grid-rowmenu-icon"
            iconProps={{ iconName: 'More' }}
            menuProps={{
                items: menuItems,
                onMenuOpened: () => {
                    refetch(); // Refetch sets the state of the query to enabled automatically
                },
            }}
        />
    );
};

export default WithStore<IContractRowMenuProps>(ContractRowMenu);

// generate menuItems for the contract row menu
const useMenuItems = ({
    contractId,
    gridType,
    isProjectOwner,
    allProperties,
    uiSettings,
    contractExists,
    selectedSticky,
    setSelectedSticky,
    store,
    openDialog,
}): IContextualMenuItem[] => {
    const { t } = useTranslation();
    // const isSuperUserOrProjectOwner = useTPPermissions({ some: true, userTypes: [UserType.ProjectOwner, UserType.SuperUser] });
    const isSuperUser = useTPPermissions({ some: true, userTypes: [UserType.SuperUser] });

    return useMemo(() => {
        const handleClickMenuItem = (sticky: Sticky) => {
            setSelectedSticky(sticky);
            allProperties.Sticky = sticky;
            Plugin.InvokeNoResponse(PluginIDs.DeltaUpsert, buildStickyDto(sticky, contractId, allProperties));
        };

        const buildStickyMenuItem = (key: string, languageKey: string, sticky: Sticky): IContextualMenuItem => ({
            key,
            text: t(`Stickyness.${languageKey}`),
            title: t(`Stickyness.${languageKey}Description`),
            disabled: selectedSticky === sticky,
            onClick: () => handleClickMenuItem(sticky),
        });

        const menuItems: IContextualMenuItem[] = [];

        if (gridType === GridType.Request || (gridType === GridType.Allocation && isProjectOwner)) {
            if (!allProperties.IsAdmin) {
                // create matching request
                if (uiSettings.settings.enableCreateMatchingRequest) {
                    allProperties.MatchType = 0;
                    menuItems.push({
                        key: 'matchingrequest',
                        text: t('ContractRowMenu.MenuItemText'),
                        title: contractExists ? t('ContractRowMenu.MenuItemTitle') : t('ContractRowMenu.ContractDoesNotExistYet'),
                        iconProps: { iconName: 'DependencyAdd' },
                        disabled: !contractExists,
                        onClick: () => {
                            const info = new RowMenuItemInfo(contractId, null, gridType, null, allProperties);
                            store.Set({ ShowMatchingActivityDialog: true, MatchingActivityInfo: info });
                        },
                    });
                }
            }
        }
        if (gridType === GridType.Request || gridType === GridType.Allocation) {
            // move contract
            if (uiSettings.settings.enableMoveContract) {
                let title = t('ContractRowMenu.ContractDoesNotExistYet');
                if (!isSuperUser && !isProjectOwner) {
                    title = t('ContractRowMenu.OnlySuperUsersAndProjectOwners');
                } else if (contractExists) {
                    title = t('ContractRowMenu.MoveContractItemTitle');
                }
                menuItems.push({
                    key: 'movecontract',
                    text: t('ContractRowMenu.MoveContractItemText'),
                    title,
                    iconProps: { iconName: 'Share' },
                    disabled: !contractExists || (!isSuperUser && !isProjectOwner),
                    onClick: () => {
                        openDialog(<MoveContractDialog ContractId={contractId} isFromContractOverview={false} />, {
                            title: t('ContractRowMenu.MoveContractToAnotherProject', { name: allProperties.ContractName }),
                            subText: t('ContractRowMenu.MoveContractSubtext'),
                            dismissEnable: false,
                            isBlocking: true,
                            styles: { main: { maxWidth: '500px' } },
                        });
                    },
                });
            }
        }

        if (gridType === GridType.Request) {
            // stickyness
            menuItems.push({
                key: 'sticky',
                text: t('ContractRowMenu.StickyItemText'),
                title: contractExists ? t('ContractRowMenu.StickyItemTitle') : t('ContractRowMenu.ContractDoesNotExistYet'),
                iconProps: { iconName: 'Pinned' },
                disabled: !contractExists,
                subMenuProps: {
                    items: [
                        buildStickyMenuItem('stickyoff', 'Off', Sticky.Off),
                        buildStickyMenuItem('stickyalways', 'Always', Sticky.Always),
                        buildStickyMenuItem('stickybefore', 'ActivitiesBefore', Sticky.ActivitiesBefore),
                        buildStickyMenuItem('stickyafter', 'ActivitiesAfter', Sticky.ActivitiesAfter),
                        buildStickyMenuItem('stickybeforeorafter', 'ActivitiesBeforeOrAfter', Sticky.ActivitiesBeforeOrAfter),
                    ],
                },
            });

            if (
                OverviewContext.Settings.EnableFindAvailableCapacity &&
                [ResourceType.Generic, ResourceType.Pool, undefined].includes(allProperties.RequestedResourceType)
            ) {
                menuItems.push({
                    key: 'magicbutton',
                    text: t('Common.FindAvailableCapacity'),
                    title: contractExists ? t('Common.FindAvailableCapacity') : t('ContractRowMenu.ContractDoesNotExistYet'),
                    iconProps: { iconName: 'Calculator' },
                    disabled: !contractExists,
                    onClick: () => {
                        const info = new RowMenuItemInfo(contractId, null, gridType, allProperties.ProjectId, allProperties);
                        store.Set({ FindAvailableCapacityDialog: true, FindAvailableCapacityInfo: info });
                    },
                });
            }
        }

        if (DebugOverrides.Enabled && [GridType.Request, GridType.Allocation].includes(gridType)) {
            menuItems.push({
                key: 'unrestricted',
                text: t('ContractRowMenu.Unrestricted'),
                title: t('ContractRowMenu.Unrestricted'),
                iconProps: { iconName: 'ProtectRestrict' },
                subMenuProps: {
                    items: [
                        {
                            key: 'deletecontract',
                            text: t('ContractRowMenu.DeleteEntireContract'),
                            title: t('ContractRowMenu.DeleteEntireContract'),
                            iconProps: { iconName: 'Delete' },
                            onClick: () => {
                                CountDownConfirm.Instance.Fire(
                                    t('ContractRowMenu.DeleteEntireContractConfirm'),
                                    t('ContractRowMenu.DeleteEntireContractWarning'),
                                    10,
                                    async () => {
                                        await DataService.Delete(EntityType.Contract, [{ TypeName: 'Contract', id: contractId }]);
                                        OverviewContext.RefreshProjectCapacity();
                                    },
                                    () => {},
                                );
                            },
                        },
                        {
                            key: 'deleterequests',
                            text: t('ContractRowMenu.DeleteAllRequests'),
                            title: t('ContractRowMenu.DeleteAllRequests'),
                            iconProps: { iconName: 'Delete' },
                            onClick: () => {
                                CountDownConfirm.Instance.Fire(
                                    t('ContractRowMenu.DeleteAllRequestsConfirm'),
                                    t('ContractRowMenu.CannotBeUndone'),
                                    10,
                                    async () => {
                                        await Plugin.InvokeNoResponse(
                                            PluginIDs.RemoveAllActivities,
                                            { ContractId: contractId, WorkType: WorkType.Request },
                                            t('ContractRowMenu.FailedRemovingRequests'),
                                            true,
                                        );
                                        OverviewContext.RefreshProjectCapacity();
                                    },
                                    () => {},
                                );
                            },
                        },
                        {
                            key: 'deleteallocations',
                            text: t('ContractRowMenu.DeleteAllAllocations'),
                            title: t('ContractRowMenu.DeleteAllAllocations'),
                            iconProps: { iconName: 'Delete' },
                            onClick: () => {
                                CountDownConfirm.Instance.Fire(
                                    t('ContractRowMenu.DeleteAllAllocationsConfirm'),
                                    t('ContractRowMenu.CannotBeUndone'),
                                    10,
                                    async () => {
                                        await Plugin.InvokeNoResponse(
                                            PluginIDs.RemoveAllActivities,
                                            { ContractId: contractId, WorkType: WorkType.Allocation },
                                            t('ContractRowMenu.FailedRemovingAllocations'),
                                            true,
                                        );
                                        OverviewContext.RefreshProjectCapacity();
                                    },
                                    () => {},
                                );
                            },
                        },
                        {
                            key: 'resetmailflagday',
                            text: t('ContractRowMenu.ResetMailStatusDay'),
                            title: t('ContractRowMenu.ResetMailStatusDay'),
                            iconProps: { iconName: 'Refresh' },
                            onClick: async () => {
                                await ApiCalls.resetMailFlagForVersions({ contractId: contractId, resolution: VersionResolution.Day });
                            },
                        },
                        {
                            key: 'resetmailflagweek',
                            text: t('ContractRowMenu.ResetMailStatusWeek'),
                            title: t('ContractRowMenu.ResetMailStatusWeek'),
                            iconProps: { iconName: 'Refresh' },
                            onClick: async () => {
                                await ApiCalls.resetMailFlagForVersions({ contractId: contractId, resolution: VersionResolution.Week });
                            },
                        },
                        {
                            key: 'resetmailflagmonth',
                            text: t('ContractRowMenu.ResetMailStatusMonth'),
                            title: t('ContractRowMenu.ResetMailStatusMonth'),
                            iconProps: { iconName: 'Refresh' },
                            onClick: async () => {
                                await ApiCalls.resetMailFlagForVersions({ contractId: contractId, resolution: VersionResolution.Month });
                            },
                        },
                    ],
                },
            });
        }

        if (uiSettings.settings.contractCustomProperties?.length && gridType !== GridType.WorkPackage) {
            menuItems.push({
                key: 'contractProperties',
                text: t('ContractRowMenu.ContractProperties'),
                title: contractExists ? t('ContractRowMenu.ContractProperties') : t('ContractRowMenu.ContractDoesNotExistYet'),
                iconProps: { iconName: 'Info' },
                disabled: !contractExists,
                onClick: () => {
                    openDialog(<CustomContractPropertiesDialog AllProperties={allProperties} ContractId={contractId} />, {
                        title: t('ContractRowMenu.ContractPropertiesTitle', { name: allProperties.ContractName }),
                        dismissEnable: false,
                        styles: { main: { maxWidth: '550px' } },
                    });
                },
            });
        }
        if (uiSettings.settings.contractInformationProperties?.length) {
            menuItems.push({
                key: 'contractInformation',
                text: t('ContractRowMenu.ContractInformation'),
                title: contractExists ? t('ContractRowMenu.ContractInformation') : t('ContractRowMenu.ContractDoesNotExistYet'),
                iconProps: { iconName: 'Info' },
                disabled: !contractExists,
                onClick: () => {
                    openDialog(<ContractInformationDialog AllProperties={allProperties} ContractId={contractId} />, {
                        title: t('ContractRowMenu.ContractInformationTitle', { name: allProperties.ContractName }),
                        dismissEnable: false,
                        styles: { main: { maxWidth: '500px' } },
                    });
                },
            });
        }

        return menuItems;
    }, [
        allProperties,
        contractExists,
        contractId,
        gridType,
        isProjectOwner,
        openDialog,
        selectedSticky,
        setSelectedSticky,
        store,
        uiSettings.settings.contractCustomProperties?.length,
        uiSettings.settings.contractInformationProperties?.length,
        uiSettings.settings.enableCreateMatchingRequest,
        uiSettings.settings.enableMoveContract,
        isSuperUser,
        t,
    ]);
};

const buildStickyDto = (sticky: Sticky, contractId: string, allProperties: any) => ({
    Origin: guid.empty,
    Entity: EntityType.Contract,
    Items: [
        {
            TypeName: 'Contract',
            Id: contractId,
            Stickyness: sticky,
            ProjectId: allProperties.ProjectId,
            ResourceGuid: allProperties.ResourceId,
            Name: allProperties.ContractName,
        },
    ],
    ChangedProperties: ['Id', 'Name', 'ProjectId', 'ResourceGuid', 'Stickyness'],
    Force: '2A02C94C-D08A-4F54-B215-FB3A3076E68C', // Specific force upsert key
});
